import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

const CARBON_MODAL_OPEN_CLASS = 'carbon-modal-open';
let carbonModalOpenCount = 0;

@Component({
  selector: 'app-carbon-warning-modal',
  templateUrl: './carbon-warning-modal.component.html',
  styleUrls: ['./carbon-warning-modal.component.scss'],
})
export class CarbonWarningModalComponent implements OnChanges, OnDestroy {
  @Input() active: boolean = false;
  @Input() title: string = 'Atenção';
  @Input() message: string = '';
  @Input() confirmText: string = 'Sim, confirmar';
  @Input() cancelText: string = 'Não, cancelar';
  @Input() middleText: string = ''; // Novo: texto do botão do meio
  @Input() customContent: boolean = false; // Novo: permite conteúdo customizado via ng-content
  /** Quando true, permite dropdown (ex.: ng-select) vazar do modal sem ser cortado. */
  @Input() contentOverflowVisible = false;
  @Input() size: 'default' | 'large' = 'default';
  @Input() disableClose: boolean = false; // Novo: desabilita o X e o clique fora
  /**
   * Quando true, não chama closeModal() após confirmar (fluxos async que fecham no pai).
   * Evita corrida com setTimeout(0) antes de [loading] propagar do componente pai.
   */
  @Input() skipAutoCloseOnConfirm = false;
  @Input() loading: boolean = false;
  @Input() loadingText: string = 'Processando...';
  @Input() loadingConfirmText: string = 'Processando...';
  @Input() disableConfirm: boolean = false;
  @Input() confirmAction: () => void = () => {};
  @Input() cancelAction: () => void = () => {};
  @Input() middleAction: () => void = () => {}; // Novo: ação do botão do meio

  @Output() onClose = new EventEmitter<void>();

  private scrollLocked = false;
  private readonly boundOverlayScroll = (event: Event) =>
    this.onOverlayScroll(event);

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.active) return;
    if (this.active) this.lockBackgroundScroll();
    else this.unlockBackgroundScroll();
  }

  ngOnDestroy(): void {
    this.unlockBackgroundScroll();
  }

  get safeMessage(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.message);
  }

  // Novo: verifica se tem botão do meio
  get hasMiddleButton(): boolean {
    return !!this.middleText && this.middleText.trim() !== '';
  }

  // Verifica se tem botão cancel
  get hasCancelButton(): boolean {
    return !!this.cancelText && this.cancelText.trim() !== '';
  }

  onOverlayScroll(event: Event): void {
    if (!this.active) return;

    const target = event.target as HTMLElement | null;
    const scrollable = target?.closest(
      '.carbon-modal-body, .fund-impact-list-table-wrap'
    ) as HTMLElement | null;

    if (!scrollable) {
      event.preventDefault();
      return;
    }

    if (!(event instanceof WheelEvent)) return;

    const atTop = scrollable.scrollTop <= 0 && event.deltaY < 0;
    const atBottom =
      scrollable.scrollTop + scrollable.clientHeight >=
        scrollable.scrollHeight - 1 && event.deltaY > 0;

    if (atTop || atBottom) {
      event.preventDefault();
    }
  }

  closeModal() {
    this.onClose.emit();
  }

  onConfirm() {
    if (this.loading) return; // Não faz nada se estiver em loading
    this.confirmAction();
    if (this.skipAutoCloseOnConfirm) {
      return;
    }
    // Aguarda um ciclo de detecção de mudanças para verificar se loading foi ativado
    setTimeout(() => {
      if (!this.loading) {
        this.closeModal();
      }
    }, 0);
  }

  onCancel() {
    if (this.loading) return; // Não faz nada se estiver em loading
    this.cancelAction();
    // Aguarda um ciclo de detecção de mudanças para verificar se loading foi ativado
    setTimeout(() => {
      if (!this.loading) {
        this.closeModal();
      }
    }, 0);
  }

  // Novo: ação do botão do meio
  onMiddle() {
    if (this.loading) return; // Não faz nada se estiver em loading
    this.middleAction();
    // Aguarda um ciclo de detecção de mudanças para verificar se loading foi ativado
    setTimeout(() => {
      if (!this.loading) {
        this.closeModal();
      }
    }, 0);
  }

  private lockBackgroundScroll(): void {
    if (this.scrollLocked || typeof document === 'undefined') return;
    this.scrollLocked = true;
    carbonModalOpenCount += 1;
    document.documentElement.classList.add(CARBON_MODAL_OPEN_CLASS);
    document.body.classList.add(CARBON_MODAL_OPEN_CLASS);
    document.addEventListener('wheel', this.boundOverlayScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener('touchmove', this.boundOverlayScroll, {
      passive: false,
      capture: true,
    });
  }

  private unlockBackgroundScroll(): void {
    if (!this.scrollLocked || typeof document === 'undefined') return;
    this.scrollLocked = false;
    carbonModalOpenCount = Math.max(0, carbonModalOpenCount - 1);
    document.removeEventListener('wheel', this.boundOverlayScroll, true);
    document.removeEventListener('touchmove', this.boundOverlayScroll, true);
    if (carbonModalOpenCount === 0) {
      document.documentElement.classList.remove(CARBON_MODAL_OPEN_CLASS);
      document.body.classList.remove(CARBON_MODAL_OPEN_CLASS);
    }
  }
}
